import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { InputField } from "@/components/InputField/InputField";
import {
	useCreateFormatPresetMutation,
	useUpdateFormatPresetMutation,
} from "@/pages/Private/redux/media/media.api";
import { FormatPreset, MediaKind } from "@/pages/Private/redux/media/media.schema";

interface FormatPresetModalProps {
	preset: FormatPreset | null;
	isOpened: boolean;
	handleClose: () => void;
}

const emptyForm = {
	key: "",
	label: "",
	width: 1920,
	height: 1080,
	aspectRatio: "16:9",
	mediaKind: MediaKind.BOTH,
};

export const FormatPresetModal: FunctionComponent<FormatPresetModalProps> = ({
	preset,
	isOpened,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`mediaPresets.${key}`);

	const [form, setForm] = useState(emptyForm);
	const [createPreset, { isLoading: isCreating }] = useCreateFormatPresetMutation();
	const [updatePreset, { isLoading: isUpdating }] = useUpdateFormatPresetMutation();

	useEffect(() => {
		setForm(
			preset
				? {
						key: preset.key,
						label: preset.label,
						width: preset.width,
						height: preset.height,
						aspectRatio: preset.aspectRatio,
						mediaKind: preset.mediaKind,
					}
				: emptyForm
		);
	}, [preset, isOpened]);

	const mediaKindOptions = Object.values(MediaKind).map((kind) => ({
		id: kind,
		title: ts(`mediaKind.${kind.toLowerCase()}`),
	}));

	const handleSave = async () => {
		if (preset) {
			await updatePreset({
				id: preset.id,
				label: form.label,
				width: form.width,
				height: form.height,
				aspectRatio: form.aspectRatio,
				mediaKind: form.mediaKind,
			}).unwrap();
		} else {
			await createPreset(form).unwrap();
		}

		handleClose();
	};

	return (
		<Modal
			disableSave={!form.label.trim() || !form.key.trim()}
			handleClose={handleClose}
			handleSave={handleSave}
			isLoading={isCreating || isUpdating}
			isOpened={isOpened}
			size="sm"
			submitButtonText={t("basics.save")}
			title={preset ? ts("formats.editTitle") : ts("formats.createTitle")}
		>
			<div className="space-y-3">
				<InputField
					handleChange={(event) => setForm((prev) => ({ ...prev, key: event.target.value }))}
					isDisabled={Boolean(preset)}
					label={ts("fields.key")}
					name="key"
					placeholder="16_9"
					value={form.key}
				/>
				<InputField
					handleChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
					label={ts("fields.label")}
					name="label"
					placeholder="16:9"
					value={form.label}
				/>
				<div className="flex gap-3">
					<InputField
						handleChange={(event) => setForm((prev) => ({ ...prev, width: +event.target.value }))}
						label={ts("fields.width")}
						name="width"
						type="number"
						value={form.width}
					/>
					<InputField
						handleChange={(event) => setForm((prev) => ({ ...prev, height: +event.target.value }))}
						label={ts("fields.height")}
						name="height"
						type="number"
						value={form.height}
					/>
				</div>
				<InputField
					handleChange={(event) =>
						setForm((prev) => ({ ...prev, aspectRatio: event.target.value }))
					}
					label={ts("fields.aspectRatio")}
					name="aspectRatio"
					placeholder="16:9"
					value={form.aspectRatio}
				/>
				<Dropdown
					data={mediaKindOptions}
					handleSelect={(item) => setForm((prev) => ({ ...prev, mediaKind: item.id as MediaKind }))}
					label={ts("fields.mediaKind")}
					value={mediaKindOptions.find((option) => option.id === form.mediaKind)}
				/>
			</div>
		</Modal>
	);
};
